// Cross-domain, so it lives here as a pure helper (no `createApi` dependency) rather than on the transactions
// slice. Bare tags are deliberate: their providers key off their own ids, so `id: "LIST"` would never fire.
export const transactionRipple = (id?: number) => [
  { type: "Transaction" as const, id: "LIST" },
  { type: "Transaction" as const, id: "TOTALS" },
  { type: "Transaction" as const, id: "GROUPED" },
  { type: "Transaction" as const, id: "SERIES" },
  ...(id === undefined ? [] : [{ type: "Transaction" as const, id }]),
  "Account" as const,
  "AccountSummary" as const,
  "AccountYears" as const,
  { type: "AccountBalanceHistory" as const, id: "LIST" },
  "Cashback" as const,
  "InvestmentHistory" as const,
  { type: "Investment" as const, id: "LIST" },
  "SavingHistory" as const,
  "LiabilityBalanceHistory" as const,
  "SavingsGoal" as const,
  "InvestmentGoal" as const,
  "Finances" as const,
];

// String id (`ACCOUNT_12`), not a bare number: `getHoldings` also tags per-row, and a numeric account tag
// would collide with a holding sharing its id.
export const holdingsAccountTag = (accountId: number) => ({
  type: "Holding" as const,
  id: `ACCOUNT_${accountId}`,
});

// String-namespaced for the same reason as `holdingsAccountTag`.
export const holdingTradesAccountTag = (accountId: number) => ({
  type: "HoldingTrade" as const,
  id: `ACCOUNT_${accountId}`,
});

// A linked broker's trade also writes a transfer on the linked account; `Saving` is bare because only the saving's own id keys its detail.
const linkedSettlementRipple = () => [
  ...transactionRipple(),
  "Saving" as const,
  "SavingYears" as const,
];

// A trade is money movement but **not** a transaction, so `Transaction:*` tags are absent unless a linked account settles it or cashback funds it.
// Skips `Holding:LIST` too — every `getHoldings` entry provides it, which would make the per-account tag pointless.
export const holdingTradeRipple = ({
  investmentId,
  accountId,
  movesTransactions,
}: {
  investmentId: number;
  accountId: number;
  movesTransactions: boolean;
}) => [
  holdingsAccountTag(accountId),
  holdingTradesAccountTag(accountId),
  { type: "Investment" as const, id: investmentId },
  { type: "Investment" as const, id: "LIST" },
  "InvestmentHistory" as const,
  "InvestmentGoal" as const,
  "Account" as const,
  "AccountSummary" as const,
  "AccountYears" as const,
  { type: "AccountBalanceHistory" as const, id: "LIST" },
  "Finances" as const,
  ...(movesTransactions ? linkedSettlementRipple() : []),
];

// The mutation only knows an instrument id, but the server revalues every account holding it — hence bare tags.
export const priceOverrideRipple = () => [
  "Holding" as const,
  "Investment" as const,
  "InvestmentHistory" as const,
  "InvestmentGoal" as const,
  "Account" as const,
  "AccountSummary" as const,
  { type: "AccountBalanceHistory" as const, id: "LIST" },
  "Finances" as const,
];

// The only write that's both a transactions row and a ledger cash event. Not self-contained — `createTransaction`'s
// own `transactionRipple` already fires, so this adds only the broker-scoped tags on top. Don't fold it in here.
export const brokerageTransferTags = ({
  investmentId,
  accountId,
}: {
  investmentId: number;
  accountId: number;
}) => [
  { type: "InvestmentTransfer" as const, id: investmentId },
  { type: "Investment" as const, id: investmentId },
  { type: "Investment" as const, id: "LIST" },
  holdingsAccountTag(accountId),
  holdingTradesAccountTag(accountId),
];
